
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, updateProfile, signOut } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { COLLECTIONS } from '../../constants/collections';

// Second instance to avoid logging out the current admin
const secondaryConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const secondaryApp = initializeApp(secondaryConfig, 'Secondary');
const secondaryAuth = getAuth(secondaryApp);

/**
 * Creates a new user in Firebase Auth and Firestore without logging out current user.
 * 
 * @param {Object} userData - { email, password, fullName, role, tenantId, ... }
 * @returns {Promise<Object>} Created user data
 */
export const adminCreateUser = async (userData) => {
    const { email, password, fullName, role, tenantId, ...rest } = userData;

    try {
        // 1. Create in secondary Auth
        const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
        const user = userCredential.user;

        // 2. Set display name
        await updateProfile(user, { displayName: fullName });

        // 3. Create document in Firestore (Tenant Profile only)
        const userDocData = {
            id: user.uid,
            email,
            fullName,
            role: role || 'guest',
            tenantId,
            isActive: true,
            status: 'active',
            createdAt: new Date(),
            ...rest
        };

        // Explicitly write to tenants/{tenantId}/users/{userId}
        await setDoc(doc(db, `tenants/${tenantId}/users`, user.uid), userDocData);

        // 4. Sign out from secondary instance immediately
        await signOut(secondaryAuth);

        return userDocData;
    } catch (error) {
        console.error('Admin create user error:', error);
        throw error;
    }
};

/**
 * Updates a user's password using the secondary instance.
 * Note: Requires the user's current password for full security, 
 * but since this is an admin action, we might need a different approach 
 * if we want to bypass that. Firebase Client SDK doesn't allow admin password reset easily.
 * Admin Reset Password usually sends an email.
 */
export const adminResetPasswordEmail = async (email) => {
    const { sendPasswordResetEmail } = await import('firebase/auth');
    try {
        await sendPasswordResetEmail(secondaryAuth, email);
    } catch (error) {
        console.error('Password reset email error:', error);
        throw error;
    }
};
